"use client"

import { useEffect, useState, useMemo, useRef } from "react"
import { useParams } from "react-router-dom"
import axios from "axios"
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Alert,
  Snackbar,
} from "@mui/material"
import {
  CalendarToday as CalendarIcon,
  MoreVert as MoreVertIcon,
   BugReport as BugIcon,
  Task as TaskIcon,
  Book as StoryIcon,
  Flag as EpicIcon,
  Flag as FlagIcon,
  Assignment as TaskAssignmentIcon,
} from "@mui/icons-material"
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, addMonths, parseISO ,format} from "date-fns"

import IssueTooltip from "./roadmapIssuetooltips"
import GanttChart from "./roadmapGanttchart"
import DependencyDialog from "./dependency"
import TaskDetailsPanel from "./taskdetailpanel"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

export const ISSUE_TYPES = {
  1: { name: "Bug", color: "#ff8a50", icon: BugIcon, bgColor: "#fff4e6" },
  2: { name: "Epic", color: "#9c27b0", icon: EpicIcon, bgColor: "#f3e5f5" },
  3: { name: "Story", color: "#2196f3", icon: StoryIcon, bgColor: "#e3f2fd" },
  4: { name: "Task", color: "#4caf50", icon: TaskIcon, bgColor: "#e8f5e9" },
  5: { name: "ChangeRequest", color: "#ff9800", icon: TaskAssignmentIcon, bgColor: "#fff3e0" },
}
const issueTypes = [
  {
    id: 1,
    name: "Bug",
    icon: <BugIcon style={{ color: "#ff8a50" }} />,
    color: "#ff8a50",
    description: "Something that is broken and needs to be fixed",
  },
  {
    id: 2,
    name: "Epic",
    icon: <EpicIcon style={{ color: "#9c27b0" }} />,
    color: "#9c27b0",
    description: "A large body of work that can be broken down into stories",
  },
  {
    id: 3,
    name: "Story",
    icon: <StoryIcon style={{ color: "#2196f3" }} />,
    color: "#2196f3",
    description: "A user story or feature requirement",
  },
  {
    id: 4,
    name: "Task",
    icon: <TaskIcon style={{ color: "#4caf50" }} />,
    color: "#4caf50",
    description: "A specific piece of work to be done",
  },
]
export const STATUS_COLORS = {
  1: "#f44336",
  2: "#ff9800", 
  3: "#4caf50",
  5: "#9e9e9e",
}

export const STATUS_NAMES = {
  1: "To Do",
  2: "In Progress", 
  3: "Done",
  5: "Cancelled",
}
const statuses = [
  { id: 1, name: "TO DO", color: "#ede54e", textColor: "#172B4D" },
  { id: 2, name: "IN PROGRESS", color: "#1972f7", textColor: "#FFFFFF" },
  { id: 3, name: "DONE", color: "#049c02", textColor: "#FFFFFF" },
  { id: 4, name: "ON HOLD", color: "#FFAB00", textColor: "#172B4D" },
  { id: 5, name: "ISSUE CLOSED", color: "#00C7BE", textColor: "#172B4D" },
  { id: 12, name: "BACKLOG", color: "#FF8B00", textColor: "#FFFFFF" },
  { id: 13, name: "PLANNING", color: "#4C9AFF", textColor: "#FFFFFF" },
  { id: 14, name: "NEW", color: "#4C9AFF", textColor: "#FFFFFF" },
]

const priorities = [
  { id: 1, name: "Highest", icon: <FlagIcon fontSize="small" style={{ color: "#FF5630" }} /> },
  { id: 2, name: "High", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
  { id: 3, name: "Medium", icon: <FlagIcon fontSize="small" style={{ color: "#FFAB00" }} /> },
  { id: 4, name: "Low", icon: <FlagIcon fontSize="small" style={{ color: "#36B37E" }} /> },
  { id: 5, name: "Lowest", icon: <FlagIcon fontSize="small" style={{ color: "#57D9A3" }} /> },
  { id: 6, name: "Normal", icon: <FlagIcon fontSize="small" style={{ color: "#FF5630" }} /> },
  { id: 7, name: "P1", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
  { id: 8, name: "P2", icon: <FlagIcon fontSize="small" style={{ color: "#FFAB00" }} /> },
  { id: 9, name: "P3", icon: <FlagIcon fontSize="small" style={{ color: "#36B37E" }} /> },
  { id: 10, name: "P4", icon: <FlagIcon fontSize="small" style={{ color: "#57D9A3" }} /> },
  { id: 11, name: "P5", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
]
export const PRIORITY_NAMES = {
  1: "Lowest",
  2: "Low",
  3: "Medium", 
  4: "High",
  5: "Highest",
}

export const PRIORITY_COLORS = {
  1: "#4caf50",
  2: "#8bc34a",
  3: "#ff9800",
  4: "#f44336",
  5: "#d32f2f",
}

const ProjectRoadmapGantt = ({sidebarOpen}) => {
  const { projectId } = useParams()

  // State management
  const [project, setProject] = useState(null)
  const [issues, setIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedEpics, setExpandedEpics] = useState({})
  const [expandedStories, setExpandedStories] = useState({})
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [currentMenuIssue, setCurrentMenuIssue] = useState(null)
  const [issueDependencies, setIssueDependencies] = useState(new Map())
  const [dependencyDialogOpen, setDependencyDialogOpen] = useState(false)
  const [currentDependencyIssue, setCurrentDependencyIssue] = useState(null)
  const [timelineView, setTimelineView] = useState("monthly")
  const [timeRange, setTimeRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(addMonths(new Date(), 11)),
  })
  const [filters, setFilters] = useState({
    issueTypes: [],
    statuses: [],
    assignees: [],
  })
  const [taskDetailsPanelOpen, setTaskDetailsPanelOpen] = useState(false)
  const [selectedTaskForPanel, setSelectedTaskForPanel] = useState(null)
  const [dependencies, setDependencies] = useState([])
  // Add these new states for overdue notifications
  const [lastOverdueCheck, setLastOverdueCheck] = useState(null)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  })

  // Fetch project data and issues
  useEffect(() => {
    fetchProjectData()
  }, [projectId])

  useEffect(() => {
    if (projectId && issues.length > 0) {
      fetchDependencies()
    }
  }, [projectId, issues])

  // Add this useEffect for overdue checking
  useEffect(() => {
    // Check for overdue issues when component mounts and every hour
    checkOverdueIssues();
    
    const interval = setInterval(() => {
      checkOverdueIssues();
    }, 60 * 60 * 1000); // Check every hour
    
    return () => clearInterval(interval);
  }, [projectId]);

  // Calculate project date range from issues
  const projectDateRange = useMemo(() => {
    if (!issues || issues.length === 0) return null

    const datesWithValues = issues
      .filter((issue) => issue.startDate && issue.endDate)
      .flatMap((issue) => [parseISO(issue.startDate), parseISO(issue.endDate)])

    if (datesWithValues.length === 0) return null

    const earliestDate = new Date(Math.min(...datesWithValues))
    const latestDate = new Date(Math.max(...datesWithValues))

    return {
      start: earliestDate,
      end: latestDate,
    }
  }, [issues])

  const projectActualDateRange = useMemo(() => {
    if (!project) return null

    const startDate = project.startDate || project.start_date || project.projectStartDate
    const endDate = project.endDate || project.end_date || project.projectEndDate

    if (!startDate || !endDate) return null

    try {
      return {
        start: parseISO(startDate),
        end: parseISO(endDate),
      }
    } catch (error) {
      //console.error("Error parsing project dates:", error)
      return null
    }
  }, [project])

  useEffect(() => {
    if (projectActualDateRange) {
      let start, end
      switch (timelineView) {
        case "weekly":
          start = startOfWeek(projectActualDateRange.start)
          end = endOfWeek(projectActualDateRange.end)
          start = addWeeks(start, -2)
          end = addWeeks(end, 2)
          break
        case "quarterly":
          start = startOfMonth(projectActualDateRange.start)
          end = endOfMonth(projectActualDateRange.end)
          const startQuarter = Math.floor(start.getMonth() / 3) * 3
          start = new Date(start.getFullYear(), startQuarter, 1)
          const endQuarter = Math.ceil((end.getMonth() + 1) / 3) * 3
          end = new Date(end.getFullYear(), endQuarter, 0)
          break
        default:
          start = startOfMonth(projectActualDateRange.start)
          end = endOfMonth(projectActualDateRange.end)
      }
      setTimeRange({ start, end })
      return
    }

    if (projectDateRange) {
      let start, end
      switch (timelineView) {
        case "weekly":
          start = startOfWeek(projectDateRange.start)
          end = endOfWeek(projectDateRange.end)
          start = addWeeks(start, -2)
          end = addWeeks(end, 2)
          break
        case "quarterly":
          start = startOfMonth(projectDateRange.start)
          end = endOfMonth(projectDateRange.end)
          const startQuarter = Math.floor(start.getMonth() / 3) * 3
          start = new Date(start.getFullYear(), startQuarter, 1)
          const endQuarter = Math.ceil((end.getMonth() + 1) / 3) * 3
          end = new Date(end.getFullYear(), endQuarter, 0)
          break
        default:
          start = startOfMonth(projectDateRange.start)
          end = endOfMonth(projectDateRange.end)
      }
      setTimeRange({ start, end })
      return
    }

    const now = new Date()
    let start, end
    switch (timelineView) {
      case "weekly":
        start = startOfWeek(now)
        end = endOfWeek(addWeeks(now, 23))
        break
      case "quarterly":
        start = startOfMonth(now)
        end = endOfMonth(addMonths(now, 23))
        break
      default:
        start = startOfMonth(now)
        end = endOfMonth(addMonths(now, 11))
    }
    setTimeRange({ start, end })
  }, [timelineView, projectActualDateRange, projectDateRange])

  // Add this function for checking overdue issues
  const checkOverdueIssues = async () => {
    try {
      // Check if we've checked in the last 30 minutes
      if (lastOverdueCheck && (Date.now() - lastOverdueCheck) < 30 * 60 * 1000) {
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) return;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await axios.get(
        `${API_URL}/api/issues/overdue/pm/notification/check-overdue`,
        {
          params: {
            project_id: projectId,
            company_id: company_id
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success && response.data.data.totalOverdue > 0) {
        // Show a brief notification to the user
        if (response.data.data.notifications.length > 0) {
          setSnackbar({
            open: true,
            message: `${response.data.data.totalOverdue} overdue issue(s) found. Managers have been notified.`,
            severity: 'warning'
          });
        }
      }

      setLastOverdueCheck(Date.now());
    } catch (error) {
      //console.error("Error checking overdue issues:", error);
    }
  };

  const fetchProjectData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const [projectResponse, issuesResponse] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
             
            }
          }
        ),
        axios.get(`${API_URL}/api/issues?project_id=${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
             
            }
          }
        ),
      ])

      const projectData = projectResponse.data?.data || projectResponse.data?.project || projectResponse.data
      const issuesData = issuesResponse.data?.data || issuesResponse.data?.issues || issuesResponse.data

      setProject(projectData)
      setIssues(Array.isArray(issuesData) ? issuesData : [])
    } catch (err) {
      //console.error("Error fetching project data:", err)
      setError("Failed to load project data. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const fetchDependencies = async () => {
    try {
      const allDependencies = []
      const dependencyMap = new Map()

      for (const issue of issues) {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }
  
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id;
          const response = await axios.get(`${API_URL}/api/issues/${issue.id}/dependencies`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
               
              }
            }
          )

          if (response.data.data && response.data.data.length > 0) {
            const transformedDependencies = response.data.data
              .map((dep) => {
                if (!dep.sourceIssue || !dep.targetIssue) {
                  return null
                }

                return {
                  id: dep.dependencyId || `${dep.sourceIssue.id}-${dep.targetIssue.id}`,
                  sourceIssueId: dep.sourceIssue.id,
                  targetIssueId: dep.targetIssue.id,
                  linkType: dep.linkType || dep.dependency_type,
                  direction: dep.direction,
                  sourceIssue: dep.sourceIssue,
                  targetIssue: dep.targetIssue,
                }
              })
              .filter((dep) => dep !== null)

            allDependencies.push(...transformedDependencies)

            transformedDependencies.forEach((dep) => {
              const linkType = dep.linkType.toLowerCase()

              if (!dependencyMap.has(dep.sourceIssueId)) {
                dependencyMap.set(dep.sourceIssueId, { blocks: [], blockedBy: [], relatesTo: [] })
              }
              if (!dependencyMap.has(dep.targetIssueId)) {
                dependencyMap.set(dep.targetIssueId, { blocks: [], blockedBy: [], relatesTo: [] })
              }

              if (linkType === "blocks") {
                dependencyMap.get(dep.sourceIssueId).blocks.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blockedBy.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === "is blocked by") {
                dependencyMap.get(dep.sourceIssueId).blockedBy.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blocks.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === "relates to") {
                dependencyMap.get(dep.sourceIssueId).relatesTo.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).relatesTo.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === "duplicates") {
                dependencyMap.get(dep.sourceIssueId).blocks.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blockedBy.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === "is duplicated by") {
                dependencyMap.get(dep.sourceIssueId).blockedBy.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blocks.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              }
            })
          }
        } catch (err) {
        }
      }

      const uniqueDependencies = allDependencies.filter(
        (dep, index, self) =>
          index ===
          self.findIndex(
            (d) =>
              String(d.sourceIssueId) === String(dep.sourceIssueId) &&
              String(d.targetIssueId) === String(dep.targetIssueId)
          ),
      )

      setDependencies(uniqueDependencies)
      setIssueDependencies(dependencyMap)
    } catch (err) {
      //console.error("Error fetching dependencies:", err)
    }
  }

  const getIssueDependencyInfo = (issueId) => {
    return issueDependencies.get(issueId) || { blocks: [], blockedBy: [], relatesTo: [] }
  }

  // Filter issues based on current filters
  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      if (filters.issueTypes.length > 0) {
        const typeMatch = filters.issueTypes.some(
          (t) => Number(t) === Number(issue.issueType) || String(t).toLowerCase() === String(issue.issueType).toLowerCase()
        )
        if (!typeMatch) return false
      }
      if (filters.statuses.length > 0) {
        const statusMatch = filters.statuses.some(
          (s) => Number(s) === Number(issue.status) || String(s).toLowerCase() === String(issue.status).toLowerCase()
        )
        if (!statusMatch) return false
      }
      return true
    })
  }, [issues, filters])

  // Organize issues into hierarchy
  const organizedIssues = useMemo(() => {
    const isEpic = (i) => Number(i.issueType) === 2 || String(i.issueType).toLowerCase() === "epic"
    const isStory = (i) => Number(i.issueType) === 3 || String(i.issueType).toLowerCase() === "story"
    const isTask = (i) => Number(i.issueType) === 4 || String(i.issueType).toLowerCase() === "task"
    const isBug = (i) => Number(i.issueType) === 1 || String(i.issueType).toLowerCase() === "bug"
    const isCR = (i) => Number(i.issueType) === 5 || String(i.issueType).toLowerCase() === "changerequest"

    const epics = filteredIssues.filter(isEpic)
    const stories = filteredIssues.filter(isStory)
    const tasks = filteredIssues.filter(isTask)
    const bugs = filteredIssues.filter(isBug)
    const changeRequests = filteredIssues.filter(isCR)

    const getEpicId = (i) => i.epic_id || i.epicId || i.epic?.id || (isEpic(i) ? null : i.parent_id)
    const getStoryId = (i) => i.story_id || i.storyId || i.story?.id

    return epics.map((epic) => {
      const epicIdStr = String(epic.id)
      return {
        ...epic,
        children: stories
          .filter((story) => String(getEpicId(story)) === epicIdStr)
          .map((story) => {
            const storyIdStr = String(story.id)
            return {
              ...story,
              children: [
                ...tasks.filter((task) => String(getStoryId(task)) === storyIdStr),
                ...bugs.filter((bug) => String(getStoryId(bug)) === storyIdStr),
                ...changeRequests.filter((cr) => String(getStoryId(cr)) === storyIdStr),
              ],
            }
          })
          .concat([
            ...tasks.filter((task) => String(getEpicId(task)) === epicIdStr && !getStoryId(task)),
            ...bugs.filter((bug) => String(getEpicId(bug)) === epicIdStr && !getStoryId(bug)),
            ...changeRequests.filter((cr) => String(getEpicId(cr)) === epicIdStr && !getStoryId(cr)),
          ]),
      }
    })
  }, [filteredIssues])

  // Event handlers
  const handleIssueNameClick = (issue) => {
    setSelectedTaskForPanel(issue)
    setTaskDetailsPanelOpen(true)
  }

  const handleTaskPanelClose = () => {
    setTaskDetailsPanelOpen(false)
    setSelectedTaskForPanel(null)
  }

  const handleTaskUpdate = (updatedIssue) => {
    setIssues((prevIssues) => prevIssues.map((issue) => (issue.id === updatedIssue.id ? updatedIssue : issue)))
  }

  const handleMenuClick = (event, issue) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setCurrentMenuIssue(issue)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setCurrentMenuIssue(null)
  }

  const handleEditDependencies = () => {
    setCurrentDependencyIssue(currentMenuIssue)
    setDependencyDialogOpen(true)
    handleMenuClose()
  }

  const handleDependencySave = (dependencyData) => {
    fetchDependencies()
    setDependencyDialogOpen(false)
    setCurrentDependencyIssue(null)
  }

  const toggleEpicExpansion = (epicId) => {
    const key = String(epicId)
    setExpandedEpics((prev) => ({
      ...prev,
      [key]: !prev[key],
      [epicId]: !prev[key],
    }))
  }

  const toggleStoryExpansion = (storyId) => {
    const key = String(storyId)
    setExpandedStories((prev) => ({
      ...prev,
      [key]: !prev[key],
      [storyId]: !prev[key],
    }))
  }

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }))
  }

  if (loading) {
    return <Typography>Loading...</Typography>
  }

  if (error) {
    return <Typography color="error">{error}</Typography>
  }

  return (
    <Box
      sx={{
        p: 3,
        position :"relative",
        // marginRight: taskDetailsPanelOpen ? "420px" : "0",
        // transition: "margin-right 0.3s ease-in-out",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {project?.name} - Roadmap
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Timeline View</InputLabel>
              <Select value={timelineView} label="Timeline View" onChange={(e) => setTimelineView(e.target.value)}>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {project && (project.startDate || project.start_date) && (project.endDate || project.end_date) && (
            <Grid item xs={12} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CalendarIcon sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    Project Timeline
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Start:</strong> {format(parseISO(project.startDate || project.start_date), "MMM dd, yyyy")}{" "}
                    |<strong> End:</strong> {format(parseISO(project.endDate || project.end_date), "MMM dd, yyyy")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Gantt Chart */}
      <GanttChart
        organizedIssues={organizedIssues}
        expandedEpics={expandedEpics}
        expandedStories={expandedStories}
        timeRange={timeRange}
        timelineView={timelineView}
        dependencies={dependencies}
        issueDependencies={issueDependencies}
        taskDetailsPanelOpen={taskDetailsPanelOpen}
        onIssueNameClick={handleIssueNameClick}
        onMenuClick={handleMenuClick}
        onToggleEpicExpansion={toggleEpicExpansion}
        onToggleStoryExpansion={toggleStoryExpansion}
        getIssueDependencyInfo={getIssueDependencyInfo}
        sidebarOpen={sidebarOpen}
      />

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose} onClick={handleMenuClose}>
        <MenuItem onClick={handleEditDependencies}>Edit Dependencies</MenuItem>
      </Menu>

      {/* Dependency Dialog */}
      {dependencyDialogOpen && currentDependencyIssue && (
        <DependencyDialog
          open={dependencyDialogOpen}
          onClose={() => {
            setDependencyDialogOpen(false)
            setCurrentDependencyIssue(null)
          }}
          onSave={handleDependencySave}
          currentIssue={currentDependencyIssue}
          allIssues={issues}
        />
      )}

      {/* Task Details Panel */}
      {taskDetailsPanelOpen && selectedTaskForPanel && (

  <TaskDetailsPanel
          issue={selectedTaskForPanel}
          onClose={handleTaskPanelClose}
          onUpdate={handleTaskUpdate}
          issueTypes={issueTypes}
          statuses={statuses}
          priorities={priorities}
          getInitials={(name) => {
            if (!name) return "UN"
            return name
              .split(" ")
              .map((word) => word[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)
          }}
        />
     
      )}

      {/* Snackbar for overdue notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ProjectRoadmapGantt